'use client'
// Regulations by region: one tab per region (with its count); under the active tab each
// regulation is a Details row showing its name and source; the verbatim text and the source link
// open below. Default state shows names only.
import React, { useId, useRef, useState } from 'react'

export type RegItem = { name: string; html: string; source?: string | null; url?: string | null }
export type RegRegion = { name: string; items: RegItem[] }

const Chevron = () => (
  <svg className="hb-det-chev" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path d="M6 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

export function RegionTabs({ regions }: { regions: RegRegion[] }) {
  const [active, setActive] = useState(0)
  const base = useId()
  const tabs = useRef<(HTMLButtonElement | null)[]>([])
  if (!regions.length) return null
  const onKey = (e: React.KeyboardEvent, i: number) => {
    const n = regions.length
    const to = e.key === 'ArrowRight' ? (i + 1) % n : e.key === 'ArrowLeft' ? (i - 1 + n) % n : -1
    if (to < 0) return
    e.preventDefault()
    setActive(to)
    tabs.current[to]?.focus()
  }
  return (
    <div className="hb-regs">
      {regions.length > 1 && (
        <div className="hb-tabs" role="tablist" aria-label="Region">
          {regions.map((r, i) => (
            <button
              key={r.name}
              ref={(el) => { tabs.current[i] = el }}
              type="button"
              role="tab"
              id={`${base}-t${i}`}
              aria-selected={i === active}
              aria-controls={`${base}-p${i}`}
              tabIndex={i === active ? 0 : -1}
              className="hb-tab"
              onClick={() => setActive(i)}
              onKeyDown={(e) => onKey(e, i)}
            >
              {r.name} <span className="hb-tab-n">{r.items.length}</span>
            </button>
          ))}
        </div>
      )}
      {/* every region is in the HTML (search engines, the verbatim audit); inactive panels are hidden */}
      {regions.map((region, r) => (
        <div
          key={region.name}
          role={regions.length > 1 ? 'tabpanel' : undefined}
          id={`${base}-p${r}`}
          aria-labelledby={regions.length > 1 ? `${base}-t${r}` : undefined}
          hidden={r !== active}
          className="hb-det-list"
        >
          {region.items.map((x, i) => (
            <details key={`${region.name}-${i}`} className="hb-det">
              <summary>
                <Chevron />
                <span className="hb-det-t">{x.name}</span>
                {x.source ? <span className="hb-det-src">{x.source}</span> : null}
              </summary>
              <div className="hb-det-b hb-det-support">
                <div dangerouslySetInnerHTML={{ __html: x.html }} />
                {x.url ? <p className="hb-det-link"><a href={x.url} target="_blank" rel="noopener">{x.source || 'Source'}</a></p> : null}
              </div>
            </details>
          ))}
        </div>
      ))}
    </div>
  )
}
